import { useEffect, useState } from "react";
import CustomButton from "../pages/CustomButton";
import { BASE_URL } from "../config";
import axios from "../axiosConfig";
import { getAuthHeaders } from "../services/useAuth";

const EmployeeExpense = () => {
  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [autoFilledRows, setAutoFilledRows] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    rate_km: "",
    ta: "",
    da_hq: "",
    da_exstation: "",
    da_outstation: "",
    int_charge: "",
    vma: "",
    fooding: "",
    other_charges: "",
    phone: "",
    remarks: "",
    start_date: new Date().toISOString().split("T")[0],
    city_id: "1",
    route_type_id: "",
  });

  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editingData, setEditingData] = useState({});
  useEffect(() => {
    fetchInitialData();
  }, []);
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [divRes, stateRes, desigRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/designations/`, {
          headers: getAuthHeaders(),
        }),
      ]);

      setDivisions(divRes.data);
      setStates(stateRes.data);
      setDesignations(desigRes.data);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      alert("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedDivisions.length > 0)
        params.append("division_ids", selectedDivisions.join(","));
      if (selectedStates.length > 0)
        params.append("state_ids", selectedStates.join(","));
      if (selectedDesignations.length > 0)
        params.append("designation_ids", selectedDesignations.join(","));
      const url = `${BASE_URL}/api/expenses${params.toString() ? `?${params.toString()}` : ""
        }`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      setExpenses(response.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      alert("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };
  const handleDivisionChange = (divisionName) => {
    setSelectedDivisions((prev) =>
      prev.includes(divisionName)
        ? prev.filter((name) => name !== divisionName)
        : [...prev, divisionName]
    );
  };

  const handleStateChange = (stateId) => {
    setSelectedStates((prev) =>
      prev.includes(stateId)
        ? prev.filter((id) => id !== stateId)
        : [...prev, stateId]
    );
  };

  const handleDesignationChange = (designationId) => {
    setSelectedDesignations((prev) =>
      prev.includes(designationId)
        ? prev.filter((id) => id !== designationId)
        : [...prev, designationId]
    );
  };

  const handleFormChange = (field, value) => {
    setExpenseForm((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // ✅ MAIN FIX (like old system)
      if (field === "da_exstation") {
        updated.ta = value;
      }

      return updated;
    });
  };
  const resetForm = () => {
    setExpenseForm({
      rate_km: "",
      ta: "",
      da_hq: "",
      da_outstation: "",
      int_charge: "",
      vma: "",
      fooding: "",
      other_charges: "",
      phone: "",
      remarks: "Field work",
      start_date: new Date().toISOString().split("T")[0],
      city_id: "1",
      route_type_id: "",
      state_id: "",
    });
    setSelectedDivisions([]);
    setSelectedStates([]);
    setSelectedDesignations([]);
    setSelectedDate(null);
    setIsAutoFilled(false);
    setAutoFilledRows([]);
  };

  const handleEditExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setEditingData({
      employee_id: expense.employee_id || "",
      ta: expense.ta || 0,
      da_hq: expense.da_hq || 0,
      da_exstation: expense.da_exstation || 0,   // ✅ FIXED
      da_outstation: expense.da_outstation || 0,
      int_charge: expense.int_charge || 0,
      vma: expense.vma || 0,
      fooding: expense.fooding || 0,
      other_charges: expense.other_charges || 0,
      phone: expense.phone || "",
      remarks: expense.remarks || "",
      start_date: expense.start_date
        ? expense.start_date.split("T")[0]
        : new Date().toISOString().split("T")[0],
      city_id: expense.city_id || "1",
      route_type_id: expense.route_type_id || "",
      state_id: expense.state_id || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingExpenseId(null);
    setEditingData({});
  };

  const handleSaveEdit = async (expenseId) => {
    try {
      setSubmitting(true);
      const originalExpense = expenses.find((exp) => exp.id === expenseId);

      if (!originalExpense) {
        alert("Expense data not found");
        return;
      }
      const division = divisions.find((d) => d.id === originalExpense.division_id);
      const designation = designations.find((d) => d.id === originalExpense.designation_id);
      const state = states.find((s) => s.id === originalExpense.state_id);

      const updateData = {
        route_type_id: expenseForm.route_type_id ? Number(expenseForm.route_type_id) : null,
        rate_km: Number(expenseForm.rate_km) || 0,
        ta: Number(expenseForm.da_exstation) || 0,
        da_hq: Number(expenseForm.da_hq) || 0,
        da_exstation: Number(expenseForm.da_exstation) || 0,
        da_outstation: Number(expenseForm.da_outstation) || 0,
        int_charge: Number(expenseForm.int_charge) || 0,
        vma: Number(expenseForm.vma) || 0,
        fooding: Number(expenseForm.fooding) || 0,
        other_charges: Number(expenseForm.other_charges) || 0,
        hotel_charges: 0,
        phone: expenseForm.phone || "",
        remarks: expenseForm.remarks || "",
        start_date: expenseForm.start_date || "",
        city_id: Number(expenseForm.city_id) || 1,
        division_id: Number(division?.id) || 0,
        division: division?.name || "",
        designation_id: Number(designation?.id) || 0,
        designation: designation?.name || "",
        state_id: Number(originalExpense.state_id) || 0,
        is_custom: true,
      };

      await axios.put(`${BASE_URL}/api/expenses/${expenseId}`, updateData, {
        headers: getAuthHeaders(),
      });

      alert("Expense updated successfully!");
      setEditingExpenseId(null);
      setEditingData({});
      fetchExpenses();
    } catch (error) {
      console.error("Error updating expense:", error);
      alert("Error updating expense. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDivisions.length === 0 || selectedDesignations.length === 0) {
      alert("Please select at least one division and one designation");
      return;
    }

    try {
      setSubmitting(true);

      if (isAutoFilled && autoFilledRows.length > 0) {
        const updateRequests = autoFilledRows.map((exp) => {   // ✅ make sure it's (exp) here
          const division = divisions.find((d) => d.id === exp.division_id);
          const designation = designations.find((d) => d.id === exp.designation_id);

          const updateData = {
            rate_km: Number(expenseForm.rate_km) || 0,
            ta: Number(expenseForm.da_exstation) || 0,
            da_hq: Number(expenseForm.da_hq) || 0,
            da_exstation: Number(expenseForm.da_exstation) || 0,
            da_outstation: Number(expenseForm.da_outstation) || 0,
            int_charge: Number(expenseForm.int_charge) || 0,
            vma: Number(expenseForm.vma) || 0,
            fooding: Number(expenseForm.fooding) || 0,
            other_charges: Number(expenseForm.other_charges) || 0,
            hotel_charges: 0,
            phone: expenseForm.phone || "",
            remarks: expenseForm.remarks || "",
            start_date: expenseForm.start_date || "",
            city_id: Number(expenseForm.city_id) || 1,
            division_id: Number(division?.id) || 0,
            division: division?.name || "",
            designation_id: Number(designation?.id) || 0,
            designation: designation?.name || "",
            state_id: Number(exp.state_id) || 0,   // ✅ exp not expense
            is_custom: true,
          };

          return axios.put(`${BASE_URL}/api/expenses/${exp.id}`, updateData, {   // ✅ exp.id
            headers: getAuthHeaders(),
          });
        });

        await Promise.all(updateRequests);
        alert("Expenses updated successfully!");
      } else {
        const addRequests = selectedDivisions.flatMap((divObj) =>
          selectedDesignations.flatMap((desId) =>
            selectedStates.map((stateId) => {

              const designation = designations.find(
                (d) => d.id === Number(desId)
              );

              const newExpense = {
                rate_km: Number(expenseForm.rate_km) || 0,
                ta: Number(expenseForm.da_exstation) || 0,
                da_hq: Number(expenseForm.da_hq) || 0,
                da_exstation: Number(expenseForm.da_exstation) || 0,
                da_outstation: Number(expenseForm.da_outstation) || 0,
                int_charge: Number(expenseForm.int_charge) || 0,
                vma: Number(expenseForm.vma) || 0,
                fooding: Number(expenseForm.fooding) || 0,
                other_charges: Number(expenseForm.other_charges) || 0,
                hotel_charges: 0,
                phone: expenseForm.phone || "",
                remarks: expenseForm.remarks || "",
                start_date: expenseForm.start_date || "",
                city_id: Number(expenseForm.city_id) || 1,
                division_id: divObj.id,
                division: divObj.name,

                designation_id: Number(desId),
                designation: designation?.name || "",
                state_id: Number(stateId),
                is_custom: true,
              };

              console.log("FINAL PAYLOAD:", newExpense);

              return axios.post(`${BASE_URL}/api/expenses`, newExpense, {
                headers: getAuthHeaders(),
              });
            })
          )
        );

        await Promise.all(addRequests);
        alert("Expenses added successfully!");
      }

      resetForm();
      fetchExpenses();
    } catch (error) {
      console.error("Error saving expenses:", error);
      alert("Error saving expenses. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditingDataChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteExpense = async (expenseId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this expense?"
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await axios.delete(`${BASE_URL}/api/expenses/${expenseId}`, {
        headers: getAuthHeaders(),
      });

      alert("Expense deleted successfully!");
      fetchExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
      alert("Error deleting expense. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const calculateTotal = (expense) => {
    return (
      (Number(expense.ta) || 0) +
      (Number(expense.da_hq) || 0) +
      (Number(expense.da_exstation) || 0) +
      (Number(expense.da_outstation) || 0) +
      (Number(expense.int_charge) || 0) +
      (Number(expense.vma) || 0) +
      (Number(expense.fooding) || 0) +
      (Number(expense.other_charges) || 0)
    );
  };
  const getExpenseInfo = (expense) => {
    const division = divisions.find((d) => d.id === expense.division_id);
    const designation = designations.find(
      (d) => d.id === expense.designation_id
    );
    const state = states.find((s) => s.id === expense.state_id);

    return {
      division: division?.name || "-",
      designation: designation?.name || "-",
      state: state?.name || "-",
    };
  };

  const handleRowSelectByDate = (row) => {
    const date = row.start_date.split("T")[0];
    setSelectedDate(date);
    setIsAutoFilled(true);
    const matchingRows = expenses.filter(
      (exp) => exp.start_date.split("T")[0] === date
    );

    setAutoFilledRows(matchingRows);

    if (matchingRows.length > 0) {
      const divisionNames = matchingRows
        .map((exp) => {
          const d = divisions.find((div) => div.id === Number(exp.division_id));
          return d ? d.name : null;
        })
        .filter(Boolean);

      const stateIds = matchingRows
        .map((exp) => exp.state_id?.toString())
        .filter(Boolean);

      const designationIds = matchingRows
        .map((exp) => exp.designation_id?.toString())
        .filter(Boolean);
      setSelectedDivisions([...new Set(divisionNames)]);
      setSelectedStates([...new Set(stateIds)]);
      setSelectedDesignations([...new Set(designationIds)]);
      const first = matchingRows[0];
      setExpenseForm({
        route_id: first.route_id || "",
        rate_km: first.rate_km || "",
        ta: first.ta || "",
        da_hq: first.da_hq || "",
        da_outstation: first.da_outstation || "",
        int_charge: first.int_charge || "",
        vma: first.vma || "",
        fooding: first.fooding || "",
        other_charges: first.other_charges || "",
        phone: first.phone || "",
        remarks: first.remarks || "Field work",
        start_date: date,
        city_id: first.city_id || "1",
        route_type_id: first.route_type_id || "",
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center border-b pb-3 mb-6">
            <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
              <span class="text-blue-700"> ADD </span>
              <span class="text-blue-500">EXPENSE</span>
            </h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-700 mr-2">
                  Applicable From
                </label>
                <input
                  type="date"
                  value={expenseForm.start_date}
                  onChange={(e) =>
                    handleFormChange("start_date", e.target.value)
                  }
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                />
              </div>
              <span className="text-xs text-red-600">
                * Means Mandatory Field
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="mb-8 border border-solid border-gray-300 rounded">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">
                Division
              </h3>
              <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
                {divisions.map((division) => (
                  <label
                    key={division.id}
                    className="flex items-center space-x-3 text-sm hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDivisions.some(
                        (d) => d.name === division.name && d.id === division.id
                      )}
                      onChange={() =>
                        handleDivisionChange({
                          name: division.name,
                          id: division.id,
                        })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 font-medium">
                      {division.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-8 border border-solid border-gray-300 rounded">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">
                State
              </h3>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {states.map((state) => (
                  <label
                    key={state.id}
                    className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStates.includes(state.id.toString())}
                      onChange={() => handleStateChange(state.id.toString())}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{state.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-8 border border-solid border-gray-300 rounded">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">
              Designation
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-h-60 overflow-y-auto pr-2">


              {designations.map((designation) => (
                <label
                  key={designation.id}
                  className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedDesignations.includes(
                      designation.id.toString()
                    )}
                    onChange={() =>
                      handleDesignationChange(designation.id.toString())
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 truncate">
                    {designation.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-4 p-3 bg-blue-50 rounded">
            <p className="text-sm text-blue-800">
              Selected: {selectedDivisions.length} divisions,{" "}
              {selectedStates.length} states, {selectedDesignations.length}{" "}
              designations
            </p>
            {isAutoFilled && (
              <p className="text-sm text-green-600 mt-1">
                Auto-fill mode: Editing {autoFilledRows.length} expenses with
                date {selectedDate}
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">
              Master Allowance
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    RATE/K.M.*
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.rate_km}
                    onChange={(e) =>
                      handleFormChange("rate_km", e.target.value)
                    }
                    className="w-full border-2 border-red-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    DA HQ:
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.da_hq}
                    onChange={(e) => handleFormChange("da_hq", e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    DA EX HQ:
                  </label>
                  <input
                    // type="number"
                    // step="0.01"
                    // value={expenseForm.ta}
                    // onChange={(e) => handleFormChange("ta", e.target.value)}
                    type="number"
                    step="0.01"
                    value={expenseForm.da_exstation}
                    onChange={(e) => handleFormChange("da_exstation", e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    DA OUT STATION:
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.da_outstation}
                    onChange={(e) =>
                      handleFormChange("da_outstation", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    PHONE*
                  </label>
                  <input
                    type="text"
                    value={expenseForm.phone}
                    onChange={(e) => handleFormChange("phone", e.target.value)}
                    className="w-full border-2 border-red-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    INTERNET CHARGE:
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.int_charge}
                    onChange={(e) =>
                      handleFormChange("int_charge", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    FOODING
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.fooding}
                    onChange={(e) =>
                      handleFormChange("fooding", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    MISC VMA
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.vma}
                    onChange={(e) => handleFormChange("vma", e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    OTHER:
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.other_charges}
                    onChange={(e) =>
                      handleFormChange("other_charges", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-2 lg:col-span-3 xl:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    REMARKS
                  </label>
                  <textarea
                    value={expenseForm.remarks}
                    onChange={(e) =>
                      handleFormChange("remarks", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-yellow-50"
                    placeholder="Field work"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex justify-center space-x-4 pt-6 border-t">
                <CustomButton
                  label={isAutoFilled ? "Update" : "Submit"}
                  onClick={handleSubmit}
                  variant="save"
                  disabled={
                    submitting ||
                    selectedDivisions.length === 0 ||
                    selectedDesignations.length === 0
                  }
                  type="submit"
                />
                <CustomButton
                  label="Cancel"
                  onClick={resetForm}
                  variant="cancel"
                  type="button"
                />
              </div>
            </form>
          </div>
        </div>

        <div className="border rounded shadow  table-wrapper">
          <div className="px-6 py-4 border-b-2 border-gray-300 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              Expense Management ({expenses.length})
            </h3>
            <CustomButton
              label="Refresh"
              variant="Refresh"
              onClick={fetchExpenses}
              className="bg-green-500 text-white"
              type="button"
            />
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mt-2">Loading expenses...</p>
            </div>
          ) : expenses.length > 0 ? (
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Select
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Division
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Designation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    State
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    TA
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    DA HQ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    DA Out
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    DA EX
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Internet
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    VMA
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Fooding
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Other
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Remarks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => {
                  const expenseInfo = getExpenseInfo(expense);
                  const isEditing = editingExpenseId === expense.id;

                  return (
                    <tr
                      key={expense.id}
                      className={`hover:bg-gray-50 ${isEditing ? "bg-blue-50" : ""
                        }`}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={
                            selectedDate === expense.start_date.split("T")[0]
                          }
                          onChange={() => handleRowSelectByDate(expense)}
                        />
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm border border-gray-300">
                        <div className="font-medium text-gray-900">
                          {expense.id}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {expenseInfo.division}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {expenseInfo.designation}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {expenseInfo.state}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editingData.start_date}
                            onChange={(e) =>
                              handleEditingDataChange(
                                "start_date",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          new Date(expense.start_date).toLocaleDateString()
                        )}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.ta}
                            onChange={(e) =>
                              handleEditingDataChange("ta", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.ta || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.da_hq}
                            onChange={(e) =>
                              handleEditingDataChange("da_hq", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.da_hq || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.da_exstation}
                            onChange={(e) =>
                              handleEditingDataChange("da_exstation", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.da_exstation || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.da_outstation}
                            onChange={(e) =>
                              handleEditingDataChange(
                                "da_outstation",
                                e.target.value
                              )
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.da_outstation || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.int_charge}
                            onChange={(e) =>
                              handleEditingDataChange(
                                "int_charge",
                                e.target.value
                              )
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.int_charge || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.vma}
                            onChange={(e) =>
                              handleEditingDataChange("vma", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.vma || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.fooding}
                            onChange={(e) =>
                              handleEditingDataChange("fooding", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.fooding || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={editingData.other_charges}
                            onChange={(e) =>
                              handleEditingDataChange(
                                "other_charges",
                                e.target.value
                              )
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          `₹${expense.other_charges || 0}`
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingData.phone}
                            onChange={(e) =>
                              handleEditingDataChange("phone", e.target.value)
                            }
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          expense.phone || "-"
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-32 border border-gray-300">
                        {isEditing ? (
                          <textarea
                            value={editingData.remarks}
                            onChange={(e) =>
                              handleEditingDataChange("remarks", e.target.value)
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            rows="2"
                          />
                        ) : (
                          <div className="truncate" title={expense.remarks}>
                            {expense.remarks || "-"}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-700 border border-gray-300">
                        ₹
                        {isEditing
                          ? (Number.parseFloat(editingData.ta) || 0) +
                          (Number.parseFloat(editingData.da_hq) || 0) +
                          (Number.parseFloat(editingData.da_outstation) || 0) +
                          (Number.parseFloat(editingData.da_exstation) || 0) +   // ✅ FIXED
                          (Number.parseFloat(editingData.int_charge) || 0) +
                          (Number.parseFloat(editingData.vma) || 0) +
                          (Number.parseFloat(editingData.fooding) || 0) +
                          (Number.parseFloat(editingData.other_charges) || 0)
                          : calculateTotal(expense)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium border border-gray-300">
                        <div className="flex space-x-2">
                          {isEditing ? (
                            <>
                              <CustomButton
                                label="Save"
                                onClick={() => handleSaveEdit(expense.id)}
                                variant="save"
                                disabled={submitting}
                                type="button"
                              />
                              <CustomButton
                                label="Cancel"
                                onClick={handleCancelEdit}
                                variant="cancel"
                                type="button"
                              />
                            </>
                          ) : (
                            <>
                              <CustomButton
                                label="Edit"
                                onClick={() => handleEditExpense(expense)}
                                variant="edit"
                                type="button"
                              />
                              <CustomButton
                                label="Delete"
                                onClick={() => handleDeleteExpense(expense.id)}
                                variant="delete"
                                type="button"
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No expenses found</p>
              <p className="text-sm">
                Select filters to view expenses or add new expenses
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeExpense;